import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useReducedMotion } from '../../hooks/useReducedMotion';

const DROPS_ROW_1 = [
  { route: 'JFK → LAX', airline: 'Delta', saved: 127 },
  { route: 'SFO → NRT', airline: 'United', saved: 214 },
  { route: 'ORD → MIA', airline: 'American', saved: 89 },
  { route: 'BOS → SJU', airline: 'JetBlue', saved: 156 },
  { route: 'ATL → CDG', airline: 'Delta', saved: 298 },
  { route: 'SEA → HNL', airline: 'Alaska', saved: 173 },
];

const DROPS_ROW_2 = [
  { route: 'EWR → LHR', airline: 'United', saved: 341 },
  { route: 'DFW → CUN', airline: 'American', saved: 94 },
  { route: 'MSP → FCO', airline: 'Delta', saved: 267 },
  { route: 'DEN → PHX', airline: 'Southwest', saved: 52 },
  { route: 'JFK → SFO', airline: 'JetBlue', saved: 118 },
  { route: 'IAH → GRU', airline: 'United', saved: 389 },
];

function TickerItem({ route, airline, saved }: { route: string; airline: string; saved: number }) {
  return (
    <div className="flex-shrink-0 flex items-center gap-3 px-5 py-3 bg-white dark:bg-stone-900 rounded-full border border-stone-200/60 dark:border-stone-700/50 shadow-sm">
      <span className="text-sm font-medium font-mono text-stone-900 dark:text-stone-100">{route}</span>
      <span className="text-xs text-stone-400 dark:text-stone-500">{airline}</span>
      <span className="text-sm font-data tabular-nums font-medium text-emerald-600 dark:text-emerald-400">-${saved}</span>
    </div>
  );
}

function TickerRow({ drops, reverse }: { drops: typeof DROPS_ROW_1; reverse?: boolean }) {
  const reduced = useReducedMotion();
  const direction = reverse ? 'animate-[ticker-right_30s_linear_infinite]' : 'animate-[ticker-left_30s_linear_infinite]';

  return (
    <div className="overflow-hidden">
      <div className={`flex gap-4 w-max ${reduced ? '' : direction}`}>
        {/* Duplicate items for seamless loop */}
        {[...drops, ...drops].map((drop, i) => (
          <TickerItem key={i} {...drop} />
        ))}
      </div>
    </div>
  );
}

export function ComparisonSection() {
  const heading = useScrollReveal();

  return (
    <section className="relative py-24 px-4 sm:px-6 bg-stone-50 dark:bg-stone-950 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div
          ref={heading.ref}
          className={`text-center mb-12 transition-[opacity,transform] duration-500 ease-out ${heading.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <h2 className="text-3xl sm:text-4xl font-serif text-stone-900 dark:text-stone-100 mb-4">
            Why Doesn't Everyone Do This?
          </h2>
          <p className="text-lg text-stone-500 dark:text-stone-400 max-w-2xl mx-auto">
            These are price drops we've found. Most travelers never know.
          </p>
        </div>
      </div>

      {/* Full-bleed ticker — breaks out of max-w container */}
      <div className="space-y-4 mb-12">
        <TickerRow drops={DROPS_ROW_1} />
        <TickerRow drops={DROPS_ROW_2} reverse />
      </div>

      <div className="max-w-4xl mx-auto">
        <p className="text-center text-stone-500 dark:text-stone-400">
          Google Flights, Kayak, Hopper — they all stop tracking once you book.
        </p>
      </div>
    </section>
  );
}
